import type { JSX, MouseEventHandler } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { FlexProps, TextProps } from '@chakra-ui/react';
import { Box, Flex, Text } from '@chakra-ui/react';

import { useIsOverflowing } from '@izimi/hooks';

import type { CustomIconName } from '../../icon';
import { CustomIcon } from '../../icon';
import { BaseModal } from '../../modals';

type Props = {
  title: string;
  value: string | JSX.Element;
  ellipsis?: boolean;
  onTitleInfoClick?: MouseEventHandler<HTMLDivElement>;
};

const ELLIPSIS_TEXT_PROPS: TextProps = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const NabanDetailsListItem = ({
  title,
  value,
  onTitleInfoClick,
  ellipsis = false,
  ...props
}: Props & FlexProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { ref, isOverflowing } = useIsOverflowing({ value });

  const openModal = useCallback<MouseEventHandler<HTMLDivElement>>((e) => {
    e.preventDefault();
    setIsModalOpen(true);
  }, []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const buttonIconName = useMemo<CustomIconName>(() => {
    if (ellipsis && isOverflowing) {
      return 'eye';
    }

    return 'info';
  }, [ellipsis, isOverflowing]);

  const _onTitleInfoClick = useMemo(() => {
    if (onTitleInfoClick) {
      return onTitleInfoClick;
    }

    if (ellipsis && isOverflowing) {
      return openModal;
    }

    return undefined;
  }, [ellipsis, isOverflowing, onTitleInfoClick, openModal]);

  return (
    <>
      <Flex flexDirection='column' alignItems='flex-start' {...props} minW={0} maxWidth='100%'>
        <Flex
          as={_onTitleInfoClick ? 'button' : undefined}
          alignItems='center'
          gap={1}
          onClick={_onTitleInfoClick}>
          <Text
            variant='tableHead'
            borderColor='neutral200'
            textAlign='left'
            textDecoration={_onTitleInfoClick ? 'underline dotted' : undefined}
            textUnderlineOffset={_onTitleInfoClick ? '.3rem' : undefined}>
            {`${title.split(' ').slice(0, -1).join(' ')} `}
            <Box as='span' display='inline' whiteSpace='nowrap'>
              {title.split(' ').slice(-1)}
              {_onTitleInfoClick && (
                <CustomIcon
                  name={buttonIconName}
                  size={16}
                  unfilled
                  display='inline'
                  verticalAlign='middle'
                  ml={1}
                />
              )}
            </Box>
          </Text>
        </Flex>
        <Text
          ref={ref}
          variant='tableCell'
          whiteSpace='pre-line'
          width='100%'
          {...(ellipsis && ELLIPSIS_TEXT_PROPS)}>
          {value}
        </Text>
      </Flex>
      {ellipsis && isModalOpen && (
        <BaseModal
          title={title}
          RenderBody={<Text variant='tableCell'>{value}</Text>}
          onClose={closeModal}
        />
      )}
    </>
  );
};
